import React, { useCallback, useMemo, useState } from 'react';
import { Mail, MessageSquareText, Send } from 'lucide-react';

import { loadMessageDeliveries } from './api';
import { tx } from './i18n';
import {
  Empty, Failed, Head, Loading, Pill, SummaryBar, SummaryItem, XomicsSelect,
} from './parts';
import { useLiveResource } from './useLive';

const PAGE_SIZE = 100;
const TONES = { PENDING: 'warn', SENDING: 'info', SENT: 'ok', FAILED: 'bad', SKIPPED: 'flat' };

function when(value) {
  if (value === null) return '—';
  return new Date(Number(value)).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export default function XomicsMessaging() {
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [offset, setOffset] = useState(0);
  const load = useCallback(() => loadMessageDeliveries({
    status, channel, limit: PAGE_SIZE, offset,
  }), [channel, offset, status]);
  const resource = useLiveResource(['messaging'], load);
  const rows = resource.data?.deliveries || [];
  const totals = useMemo(() => {
    const result = { PENDING: 0, SENDING: 0, SENT: 0, FAILED: 0, SKIPPED: 0 };
    (resource.data?.summary || []).forEach((entry) => {
      result[entry.status] = (result[entry.status] || 0) + entry.total;
    });
    return result;
  }, [resource.data]);

  function filter(setter, value) {
    setter(value);
    setOffset(0);
  }

  return (
    <div className="xo-platform-page xo-messaging-page">
      <Head title={tx('ui.messaging.title')} subtitle={tx('ui.messaging.subtitle')} />
      <SummaryBar>
        <SummaryItem value={totals.PENDING + totals.SENDING} label={tx('ui.messaging.in_progress')} tone="warn" />
        <SummaryItem value={totals.SENT} label={tx('ui.messaging.sent')} />
        <SummaryItem value={totals.FAILED} label={tx('ui.messaging.failed')} tone={totals.FAILED ? 'warn' : 'flat'} />
        <SummaryItem value={resource.data?.total || 0} label={tx('ui.messaging.filtered_total')} />
      </SummaryBar>
      <div className="xo-bar xo-messaging-filters">
        <div className="xo-bar-group">
          <label>{tx('ui.messaging.status')}
            <XomicsSelect value={status} onChange={(event) => filter(setStatus, event.target.value)}>
              <option value="">{tx('ui.messaging.all_statuses')}</option>
              {['PENDING', 'SENDING', 'SENT', 'FAILED', 'SKIPPED'].map((value) => (
                <option key={value} value={value}>{tx(`ui.messaging.status_${value.toLowerCase()}`)}</option>
              ))}
            </XomicsSelect>
          </label>
          <label>{tx('ui.messaging.channel')}
            <XomicsSelect value={channel} onChange={(event) => filter(setChannel, event.target.value)}>
              <option value="">{tx('ui.messaging.all_channels')}</option>
              <option value="EMAIL">{tx('ui.messaging.email')}</option>
              <option value="SMS">{tx('ui.messaging.sms')}</option>
            </XomicsSelect>
          </label>
        </div>
      </div>
      <section className="xo-data-surface">
        {resource.loading && resource.data === null ? (
          <Loading label={tx('ui.messaging.loading')} />
        ) : resource.error ? (
          <Failed message={resource.error} onRetry={resource.refresh} />
        ) : rows.length === 0 ? (
          <Empty title={tx('ui.messaging.empty')} detail={tx('ui.messaging.empty_detail')} />
        ) : (
          <div className="xo-table-wrap">
            <table className="xo-table xo-messaging-table">
              <thead><tr>
                <th>{tx('ui.messaging.event')}</th>
                <th>{tx('ui.messaging.channel')}</th>
                <th>{tx('ui.messaging.destination')}</th>
                <th>{tx('ui.messaging.status')}</th>
                <th>{tx('ui.messaging.attempts')}</th>
                <th>{tx('ui.messaging.updated')}</th>
              </tr></thead>
              <tbody>{rows.map((row) => (
                <tr key={row.id}>
                  <td><strong>{row.title || row.templateCode || row.eventCode || row.eventKey}</strong>
                    <span className="xo-cell-note">
                      {row.orderReference || row.shipmentTrackingNumber || row.eventKey}
                    </span>
                  </td>
                  <td><span className="xo-message-channel">
                    {row.channel === 'EMAIL' ? <Mail aria-hidden="true" /> : <MessageSquareText aria-hidden="true" />}
                    {row.channel}
                  </span></td>
                  <td><strong>{row.destination}</strong><span className="xo-cell-note">{row.provider || '—'}</span></td>
                  <td><Pill tone={TONES[row.status] || 'flat'}>
                    {row.status === 'SENDING' && <Send aria-hidden="true" />}
                    {tx(`ui.messaging.status_${row.status.toLowerCase()}`)}
                  </Pill>{row.failureCode && <span className="xo-cell-note t-bad">{row.failureCode}</span>}</td>
                  <td className="xo-num">{row.attempts}</td>
                  <td><strong>{when(row.updatedAt)}</strong>{row.nextAttemptAt && <span className="xo-cell-note">{tx('ui.messaging.retry_at', { time: when(row.nextAttemptAt) })}</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
      {resource.data && resource.data.total > 0 && (
        <div className="xo-foot-split">
          <span>{tx('ui.messaging.showing', {
            from: offset + 1, to: Math.min(offset + rows.length, resource.data.total), total: resource.data.total,
          })}</span>
          <div className="xo-bar-group">
            <button type="button" className="xo-link" disabled={offset === 0 || resource.loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>{tx('ui.ui.previous_50f94286')}</button>
            <button type="button" className="xo-link"
              disabled={offset + rows.length >= resource.data.total || resource.loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}>{tx('ui.ui.next_bc981983')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
